import {
  ServiceTicket,
  ServiceTicketKind,
  ServiceTicketStatus,
  useServiceTicketUpdateStatusMutation,
  useServiceTicketsQuery,
} from '@dieterApi/serviceTicket';
import { Chip, CircularProgress, FormControl, InputLabel, Link, MenuItem, Select } from '@mui/material';
import MUIDataTable, { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { ServiceTicketResolveDialog } from './ServiceTicketResolveDialog';

// Nachfolger der HubSpot-Tickets (NEW SERVICE / DSFA): einfache Arbeitsliste
// mit den gewohnten Pipeline-Status. Die Tickets leben in der App-DB —
// bewusst nicht in Chatwoot, damit die Inbox-Ansicht dort nicht flutet.

const STATUS_LABELS: Record<ServiceTicketStatus, string> = {
  NEW: 'Neu',
  WAITING_ON_CONTACT: 'Wartet auf Kunde',
  WAITING_ON_US: 'Wartet auf uns',
  CLOSED: 'Geschlossen',
};

const STATUS_COLORS: Record<ServiceTicketStatus, 'info' | 'warning' | 'primary' | 'success'> = {
  NEW: 'info',
  WAITING_ON_CONTACT: 'warning',
  WAITING_ON_US: 'primary',
  CLOSED: 'success',
};

const STATUS_ORDER: ServiceTicketStatus[] = ['NEW', 'WAITING_ON_CONTACT', 'WAITING_ON_US', 'CLOSED'];

type StatusFilter = 'open' | ServiceTicketStatus | 'all';
type KindFilter = 'all' | ServiceTicketKind;

export function AdminTickets() {
  const { enqueueSnackbar } = useSnackbar();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  // NEW_SERVICE-Tickets werden über den Dialog geschlossen (Rückmeldung an
  // den Kunden, optional Snippet in dessen Auswahl übernehmen)
  const [resolvingTicket, setResolvingTicket] = useState<ServiceTicket | null>(null);

  const { data, loading, refetch } = useServiceTicketsQuery({
    status: statusFilter === 'open' || statusFilter === 'all' ? undefined : statusFilter,
    includeClosed: statusFilter === 'all' || statusFilter === 'CLOSED',
    kind: kindFilter === 'all' ? undefined : kindFilter,
  });
  const [updateStatus] = useServiceTicketUpdateStatusMutation();

  const tickets = useMemo(() => data?.serviceTickets ?? [], [data]);

  const handleStatusChange = useCallback(
    async (ticket: ServiceTicket, status: ServiceTicketStatus) => {
      if (status === 'CLOSED' && ticket.kind === 'NEW_SERVICE') {
        setResolvingTicket(ticket);
        return;
      }
      try {
        await updateStatus({ variables: { id: ticket.id, status } });
        enqueueSnackbar(`Status geändert: ${STATUS_LABELS[status]}`, { variant: 'success' });
        refetch();
      } catch (error: any) {
        enqueueSnackbar(`Fehler beim Statuswechsel: ${error.message}`, { variant: 'error' });
      }
    },
    [updateStatus, enqueueSnackbar, refetch]
  );

  // MUIDataTable sortiert/filtert clientseitig — das Original-Ticket wird
  // deshalb über den Index in currentTableData aufgelöst, nicht über rowIndex.
  const resolveTicket = useCallback(
    (tableMeta: any): ServiceTicket | undefined => tickets[tableMeta.currentTableData?.[tableMeta.rowIndex]?.index],
    [tickets]
  );

  const columns: MUIDataTableColumn[] = useMemo(
    () => [
      {
        name: 'kind',
        label: 'Typ',
        options: {
          customBodyRender: (value: ServiceTicketKind) =>
            value === 'DSFA' ? (
              <Chip label="DSFA" color="secondary" size="small" />
            ) : (
              <Chip label="Neuer Dienst" color="default" size="small" />
            ),
        },
      },
      {
        name: 'subject',
        label: 'Vorgang',
        options: {
          customBodyRender: (_: unknown, tableMeta: any) => {
            const ticket = resolveTicket(tableMeta);
            if (!ticket) return null;
            if (ticket.kind === 'DSFA') {
              return (
                <div>
                  <div>DSFA-Fragebogen abgeschlossen</div>
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>Index: {ticket.dsfaIndex || '—'}</div>
                </div>
              );
            }
            return (
              <div>
                <div>
                  {ticket.serviceUrl ? (
                    <Link href={ticket.serviceUrl} target="_blank" rel="noopener noreferrer">
                      {ticket.serviceName}
                    </Link>
                  ) : (
                    ticket.serviceName
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#888' }}>{ticket.serviceKey}</div>
              </div>
            );
          },
        },
      },
      {
        name: 'companyName',
        label: 'Unternehmen',
      },
      {
        name: 'contactEmail',
        label: 'Kontakt',
        options: {
          customBodyRender: (value: string | null) => (value ? <Link href={`mailto:${value}`}>{value}</Link> : '—'),
        },
      },
      {
        name: 'createdAt',
        label: 'Erstellt',
        options: {
          customBodyRender: (value: string) => new Date(value).toLocaleDateString('de-DE'),
        },
      },
      {
        name: 'status',
        label: 'Status',
        options: {
          customBodyRender: (value: ServiceTicketStatus, tableMeta: any) => {
            const ticket = resolveTicket(tableMeta);
            if (!ticket) return null;
            return (
              <div>
                <Select
                  value={value}
                  size="small"
                  variant="standard"
                  disableUnderline
                  onChange={(event) => handleStatusChange(ticket, event.target.value as ServiceTicketStatus)}
                  renderValue={(status) => (
                    <Chip
                      label={STATUS_LABELS[status as ServiceTicketStatus]}
                      color={STATUS_COLORS[status as ServiceTicketStatus]}
                      size="small"
                    />
                  )}
                  sx={{ '& .MuiSelect-select': { paddingY: '2px' } }}
                >
                  {STATUS_ORDER.map((status) => (
                    <MenuItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </MenuItem>
                  ))}
                </Select>
                {ticket.customerNotifiedAt && (
                  <div style={{ fontSize: '0.75rem', color: '#888' }}>
                    benachrichtigt am {new Date(ticket.customerNotifiedAt).toLocaleDateString('de-DE')}
                  </div>
                )}
              </div>
            );
          },
        },
      },
    ],
    [resolveTicket, handleStatusChange]
  );

  const options: MUIDataTableOptions = useMemo(
    () => ({
      responsive: 'standard',
      selectableRows: 'none',
      download: false,
      print: false,
      filter: false,
      viewColumns: false,
      rowsPerPage: 50,
      rowsPerPageOptions: [25, 50, 100],
      sortOrder: { name: 'createdAt', direction: 'asc' },
      textLabels: {
        body: { noMatch: loading ? 'Laden...' : 'Keine Tickets gefunden' },
        pagination: { rowsPerPage: 'Zeilen pro Seite:', displayRows: 'von' },
        toolbar: { search: 'Suche' },
      },
    }),
    [loading]
  );

  const tableData = useMemo(
    () =>
      tickets.map((ticket) => ({
        kind: ticket.kind,
        subject:
          ticket.kind === 'DSFA' ? `DSFA ${ticket.dsfaIndex ?? ''}` : `${ticket.serviceName} ${ticket.serviceKey}`,
        companyName: ticket.companyName,
        contactEmail: ticket.contactEmail,
        createdAt: ticket.createdAt,
        status: ticket.status,
      })),
    [tickets]
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', margin: '16px 0' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="ticket-status-filter">Status</InputLabel>
          <Select
            labelId="ticket-status-filter"
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
          >
            <MenuItem value="open">Alle offenen</MenuItem>
            {STATUS_ORDER.map((status) => (
              <MenuItem key={status} value={status}>
                {STATUS_LABELS[status]}
              </MenuItem>
            ))}
            <MenuItem value="all">Alle (inkl. geschlossene)</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="ticket-kind-filter">Typ</InputLabel>
          <Select
            labelId="ticket-kind-filter"
            label="Typ"
            value={kindFilter}
            onChange={(event) => setKindFilter(event.target.value as KindFilter)}
          >
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="NEW_SERVICE">Neuer Dienst</MenuItem>
            <MenuItem value="DSFA">DSFA</MenuItem>
          </Select>
        </FormControl>
        {loading && <CircularProgress size={20} />}
        <div style={{ marginLeft: 'auto', color: '#888', fontSize: '0.875rem' }}>
          {tickets.length} Ticket{tickets.length !== 1 ? 's' : ''}
        </div>
      </div>
      <MUIDataTable title="" data={tableData} columns={columns} options={options} />
      <ServiceTicketResolveDialog
        ticket={resolvingTicket}
        onClose={() => setResolvingTicket(null)}
        onResolved={() => {
          setResolvingTicket(null);
          refetch();
        }}
      />
    </div>
  );
}
